import React, { useState, useEffect, useContext, createContext } from 'react';
import { supabase } from '../lib/supabaseClient';
import { loadingDebugger } from '../utils/debugUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    // Mevcut oturum bilgisini kontrol et
    const checkSession = async () => {
      const startTime = Date.now();
      loadingDebugger.logLoadingStart('useAuth', 'checkSession');
      
      try {
        // Timeout ekle (10 saniye)
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 10000)
        );
        
        const { data: sessionData, error: sessionError } = await Promise.race([
          sessionPromise, 
          timeoutPromise
        ]);
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (sessionData?.session?.user) {
          loadingDebugger.log('useAuth', 'SESSION_FOUND', { userId: sessionData.session.user.id });
          setUser(sessionData.session.user);
          setSession(sessionData.session);
          
          // Kullanıcı profil bilgilerini al - timeout ile
          const profilePromise = supabase
            .from('user_profiles')
            .select('*')
            .eq('id', sessionData.session.user.id)
            .single();
            
          const profileTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
          );
          
          try {
            const { data: profileData, error: profileError } = await Promise.race([
              profilePromise,
              profileTimeoutPromise
            ]);
            
            if (!profileError && profileData) {
              loadingDebugger.log('useAuth', 'PROFILE_LOADED', { profileId: profileData.id });
              setUserProfile(profileData);
            } else {
              loadingDebugger.logError('useAuth', profileError || 'No profile data', { userId: sessionData.session.user.id });
            }
          } catch (profileErr) {
            loadingDebugger.logError('useAuth', profileErr, { operation: 'profile_fetch' });
            console.warn('Profile yüklenemedi:', profileErr);
            // Profile yüklenemese bile devam et
          }
        } else {
          loadingDebugger.log('useAuth', 'NO_SESSION', {});
        }
      } catch (error) {
        loadingDebugger.logError('useAuth', error, { operation: 'checkSession' });
        console.error('Oturum kontrolü hatası:', error);
        // Hata durumunda session state'ini temizle
        setSession(null);
        setUser(null);
        setUserProfile(null);
      } finally {
        // Her durumda loading'i false yap
        const duration = Date.now() - startTime;
        loadingDebugger.logLoadingEnd('useAuth', 'checkSession', true, duration);
        setLoading(false);
      }
    };
    
    // İlk yükleme için oturum kontrolü
    checkSession();
    
    // Auth durumu değişikliklerini dinle
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setSession(session);
          
          // Kullanıcı profil bilgilerini al - timeout ile
          const profilePromise = supabase
            .from('user_profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
            
          const profileTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
          );
          
          try {
            const { data: profileData, error: profileError } = await Promise.race([
              profilePromise,
              profileTimeoutPromise
            ]);
            
            if (!profileError && profileData) {
              // Online status'u true yap - timeout ile
              const updatePromise = supabase
                .from('user_profiles')
                .update({ 
                  online_status: true,
                  updated_at: new Date().toISOString()
                })
                .eq('id', session.user.id);
                
              const updateTimeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Update timeout')), 3000)
              );
              
              try {
                await Promise.race([updatePromise, updateTimeoutPromise]);
              } catch (updateError) {
                console.warn('Online status güncellenemedi:', updateError);
              }
              
              setUserProfile({
                ...profileData,
                online_status: true
              });
            }
          } catch (profileError) {
            console.warn('Profile auth change yüklenemedi:', profileError);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      }
    });
    
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Login history logging function
  const logLoginAttempt = async (userId, success, errorMessage = null) => {
    try {
      // Get IP address and location
      const getLocationInfo = async () => {
        try {
          const ipServices = [
            'https://api.ipify.org?format=json',
            'https://ipapi.co/json/',
            'https://ipinfo.io/json'
          ];
          
          let ipAddress = 'unknown';
          let location = 'Unknown';
          
          for (const service of ipServices) {
            try {
              const response = await fetch(service, { timeout: 3000 });
              if (response.ok) {
                const data = await response.json();
                ipAddress = data.ip || data.query || 'unknown';
                
                if (ipAddress !== 'unknown') {
                  try {
                    const locationResponse = await fetch(`https://ipapi.co/${ipAddress}/json/`, { timeout: 3000 });
                    if (locationResponse.ok) {
                      const locationData = await locationResponse.json();
                      const city = locationData.city || 'Unknown';
                      const country = locationData.country_name || 'Unknown';
                      location = `${city}, ${country}`;
                    }
                  } catch (error) {
                    console.warn('Location service failed:', error);
                  }
                }
                break;
              }
            } catch (error) {
              continue;
            }
          }
          
          return { ip: ipAddress, location };
        } catch (error) {
          console.error('Location info error:', error);
          return { ip: 'unknown', location: 'Unknown' };
        }
      };

      // Get device info
      const getDeviceInfo = () => {
        const userAgent = navigator.userAgent;
        let deviceType = 'Desktop';
        let browser = 'Unknown';
        let os = 'Unknown';
        
        // Detect device type
        if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
          deviceType = /iPad/i.test(userAgent) ? 'Tablet' : 'Mobile';
        }
        
        // Detect browser
        if (userAgent.includes('Chrome')) browser = 'Chrome';
        else if (userAgent.includes('Firefox')) browser = 'Firefox';
        else if (userAgent.includes('Safari')) browser = 'Safari';
        else if (userAgent.includes('Edge')) browser = 'Edge';
        else if (userAgent.includes('Opera')) browser = 'Opera';
        
        // Detect OS
        if (userAgent.includes('Windows')) os = 'Windows';
        else if (userAgent.includes('Mac')) os = 'macOS';
        else if (userAgent.includes('Linux')) os = 'Linux';
        else if (userAgent.includes('Android')) os = 'Android';
        else if (userAgent.includes('iOS')) os = 'iOS';
        
        return {
          deviceType,
          browser,
          os,
          userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          cookieEnabled: navigator.cookieEnabled,
          online: navigator.onLine
        };
      };

      const [{ ip, location }, deviceInfo] = await Promise.all([
        getLocationInfo(),
        Promise.resolve(getDeviceInfo())
      ]);

      const insertData = {
        user_id: userId,
        ip_address: ip,
        location,
        device_info: deviceInfo,
        user_agent: navigator.userAgent,
        success
        // error_message: success ? null : (errorMessage ? errorMessage.substring(0, 255) : null) // Temporarily disabled - column doesn't exist
      };
      
      console.log('📝 useAuth: Inserting to user_login_history:', insertData);
      
      const { data, error } = await supabase
        .from('user_login_history')
        .insert([insertData])
        .select();
        
      if (error) {
        console.error('❌ useAuth: user_login_history insert error:', error);
        throw error;
      }
      
      console.log('✅ useAuth: user_login_history insert success:', data);
    } catch (error) {
      console.error('Login history logging failed:', error);
      // Don't throw - logging shouldn't break login flow
    }
  };

  // Giriş yap
  const signIn = async (email, password, captchaToken) => {
    let userId = null;
    
    try {
      // console.log('Giriş işlemi başlatılıyor:', { email, captchaToken: !!captchaToken });
      
      // CAPTCHA token kontrolü (geliştirme ortamında zorunlu, production'da isteğe bağlı)
      const isDevelopment = import.meta.env.DEV;
      if (isDevelopment && !captchaToken) {
        throw new Error('CAPTCHA doğrulaması gereklidir');
      }
      
      // Supabase ile giriş yap (CAPTCHA token'ı metaveriye eklenebilir)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken // Supabase metadata olarak gönder
        }
      });

      if (error) {
        console.error('Supabase giriş hatası:', error);
        
        // Try to get user ID for logging
        try {
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('email', email)
            .single();
          if (userProfile) {
            userId = userProfile.id;
          }
        } catch (e) {
          // Ignore error
        }
        
        // Log failed attempt
        if (userId) {
          console.log('📝 useAuth: Logging failed login attempt...');
          await logLoginAttempt(userId, false, error.message);
        } else {
          console.log('⚠️ useAuth: No userId, cannot log failed attempt');
        }
        
        throw error;
      }

      if (data.user) {
        userId = data.user.id;
        // console.log('Giriş başarılı:', data.user);
        setUser(data.user);
        
        // Kullanıcı profilini çek
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();
          
        if (!profileError && profile) {
          // Online status'u true yap
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({ 
              online_status: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', data.user.id);
            
          if (updateError) {
            console.warn('Online status güncellenemedi:', updateError);
          }
          
          localStorage.setItem('userProfile', JSON.stringify({
            ...profile,
            online_status: true
          }));
        }
        
        // Log successful attempt
        console.log('📝 useAuth: Logging successful login attempt...');
        await logLoginAttempt(userId, true);
        
        return { success: true, user: data.user };
      }
      
    } catch (error) {
      console.error('Giriş hatası:', error);
      throw error;
    }
  };

  // Kayıt ol
  const signUp = async (email, password, captchaToken) => {
    try {
      // console.log('Kayıt işlemi başlatılıyor:', { email, captchaToken: !!captchaToken });
      
      // CAPTCHA token kontrolü (geliştirme ortamında zorunlu, production'da isteğe bağlı)
      const isDevelopment = import.meta.env.DEV;
      if (isDevelopment && !captchaToken) {
        throw new Error('CAPTCHA doğrulaması gereklidir');
      }
      
      // Supabase ile kayıt ol (CAPTCHA token'ı metaveriye eklenebilir)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken // Supabase metadata olarak gönder
        }
      });
      
      if (error) {
        console.error('Supabase kayıt hatası:', error);
        throw error;
      }
      
      // console.log('Kayıt başarılı:', data);
      return data;
    } catch (error) {
      console.error('Kayıt hatası:', error);
      throw error;
    }
  };

  // Çıkış yap
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Çıkış hatası:', error);
      throw error;
    }
  };

  // Değerler
  const value = {
    user,
    session,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 